import { FormEvent, useEffect, useState, useRef } from "react";
import { NewToggleSwitch } from "../FormElement/ToggleButton";
import { InputElement } from "../FormElement/InputElement";
import { SelectElement } from "../FormElement/SelectElement";
import { ILocationGroupError, LocationGroup, LocationGroupErrorValue, LocationGroupFormValue, LocationGroupDto } from "../../models/LocationGroup";
import { IButton } from "../Button/Button";
import { useStore } from "../../stores/store";
import { useNavigate } from 'react-router-dom';
import { observer } from "mobx-react-lite";
import LoadingComponent from "../LoadingComponent";
import { AxiosResponse } from "axios";
import ServerErrorMessage from "../Message/Error/ServerErrorMessage";
import { ApiError } from "../../models/Error";
import * as yup from 'yup';
import { SelectListOption } from "../../models/Pharse2/Graph";
import { LocationDto } from "../../models/Location";
import CoustomTable, { CoustomTableHeaderProps, CoustomTableRowProps, customTableTheme } from "../Table/CoustomTable";
import { ThemeProvider } from "@mui/material/styles";

interface props {
    selectedId?: string;
}

export default observer(function LocationGroupCreateForm({ selectedId }: props) {
    const [serverError, setServerError] = useState<ApiError | undefined>();
    const { locationGroupStore, projectStore } = useStore();
    const { submitting, loadingInitial, CreateLocationGroup, EditLocationGroup, LoadSelectedLocationGroup, clearSelectedLocationGroup } = locationGroupStore;
    const { LoadingProject, selectedProject } = projectStore;

    const [record, setRecord] = useState(new LocationGroupFormValue());
    const [errors, setErrors] = useState<Partial<ILocationGroupError>>(new LocationGroupErrorValue());
    const [projectList, setProjectList] = useState<SelectListOption[]>([]);
    const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
    const [locations, setLocations] = useState<LocationDto[]>([]);
    const [locationPage, setLocationPage] = useState(0);
    const [locationRows, setLocationRows] = useState<CoustomTableRowProps[]>([]);

    const navigate = useNavigate();
    const activeId = useRef<string | null>(null);
    const direction = useRef<'asc' | 'desc'>('asc');

    // 當有 selectedId 時，加載現有的 LocationGroup 數據
    useEffect(() => {
        if (selectedId) {
            LoadSelectedLocationGroup(selectedId).then(lg => {
                if (lg) {
                    setRecord(new LocationGroupFormValue({
                        id: lg.id,
                        projectId: lg.projectId,
                        name: lg.name,
                        status: lg.status,
                        remark: lg.remark,
                        isDeleted: lg.isDeleted,
                        alertThreshold: lg.alertThreshold,
                        alarmThreshold: lg.alarmThreshold,
                        actionThreshold: lg.actionThreshold
                    }));
                    // 保存 locations 數據
                    if (lg.locations && lg.locations.length > 0) {
                        setLocations(lg.locations);
                    }
                }
            }).catch(error => {
                console.error("Error loading LocationGroup:", error);
            });
        }
    }, [selectedId, LoadSelectedLocationGroup]);

    // 將 locations 轉換為表格行
    useEffect(() => {
        if (locations.length > 0) {
            const rows: CoustomTableRowProps[] = locations.map((location) => ({
                Id: String(location.id || ''),
                data: [
                    { id: "name", data: location.name || "" },
                    { id: "projectName", data: location.projectName || "" },
                    { id: "loggerSetName", data: location.loggerSetName || "" },
                    { id: "loggerSN", data: location.loggerSN || "" },
                    { id: "address", data: location.address || "" },
                    { id: "status", data: location.status || "" },
                ],
                hiddenData: [],
                ischecked: false
            }));
            setLocationRows(rows);
        } else {
            setLocationRows([]);
        }
    }, [locations]);

    const validationSchema = yup.object({
        projectId: yup.number()
            .required('Project is required')
            .test(
                'is-not-zero',
                'Project has to be chosen',
                value => value !== 0 && value !== undefined
            ),
        name: yup.string()
            .required('Location Group name is required')
            .trim()
            .min(1, "Location Group name can't be empty"),
        status: yup.boolean().required('Status is required'),
    });

    const handleValidationChange = (field: string, value: any) => {
        validationSchema.validateAt(field, {
            [field]: value
        })
            .then(() => {
                setErrors(prev => ({ ...prev, [field]: '' }));
            })
            .catch(err => {
                console.log(err);
                setErrors(prev => ({ ...prev, [field]: err.message }));
            });
    }

    // 獲取項目列表
    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const projects = await projectStore.LoadingProjectList();
                const projectOptions: SelectListOption[] = projects
                    .filter(p => p.projectId !== undefined)
                    .map(p => ({
                        id: p.projectId!,
                        name: p.name || ""
                    }));
                setProjectList(projectOptions);
            } catch (error) {
                console.error("Error fetching projects:", error);
            }
        };
        fetchProjects();
    }, [projectStore]);

    // 當選擇項目時，加載項目詳情
    useEffect(() => {
        if (record.projectId && record.projectId !== 0) {
            LoadingProject(String(record.projectId)).then(i => {
                if (i) {
                    // Project loaded
                }
            });
        }
    }, [record.projectId, LoadingProject]);

    // 處理項目選擇
    useEffect(() => {
        if (selectedProjectIds.length > 0) {
            const num = parseInt(selectedProjectIds[0]);
            setRecord({ ...record, projectId: num });
        }
    }, [selectedProjectIds]);

    async function handleSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        try {
            await validationSchema.validate(record, { abortEarly: false });
            setErrors({});
        } catch (err) {
            if (err instanceof yup.ValidationError) {
                const newErrors: Record<string, string> = {};
                err.inner.forEach((error) => {
                    if (error.path) {
                        newErrors[error.path] = error.message;
                    }
                });
                setErrors(newErrors);
                console.log(newErrors);
            }
            return;
        }

        // 確保 projectId 和 name 存在
        if (!record.projectId || record.projectId === 0) {
            setErrors(prev => ({ ...prev, projectId: 'Project is required' }));
            return;
        }

        if (!record.name || record.name.trim() === '') {
            setErrors(prev => ({ ...prev, name: 'Location Group name is required' }));
            return;
        }

        let newRecord: LocationGroup = {
            id: record.id,
            projectId: record.projectId,
            name: record.name.trim(),
            status: record.status !== undefined ? record.status : false,
            remark: record.remark,
            isDeleted: record.isDeleted,
            alertThreshold: record.alertThreshold,
            alarmThreshold: record.alarmThreshold,
            actionThreshold: record.actionThreshold,
        };

        if (selectedId) {
            // 編輯模式
            EditLocationGroup(newRecord).then(() => navigate("/locationGroup"))
                .catch((error) => {
                    console.log(error);
                    const { data, status, config } = error.response as AxiosResponse;
                    var newError: ApiError = {
                        messageHeader: data[0],
                        messageList: data.slice(1)
                    };
                    setServerError(newError);
                });
        } else {
            // 創建模式
            CreateLocationGroup(newRecord).then(() => navigate("/locationGroup"))
                .catch((error) => {
                    console.log(error);
                    const { data, status, config } = error.response as AxiosResponse;
                    var newError: ApiError = {
                        messageHeader: data[0],
                        messageList: data.slice(1)
                    };
                    setServerError(newError);
                });
        }
    }

    const handleCancel = () => {
        if (locationGroupStore.selectedLocationGroup) clearSelectedLocationGroup();
        navigate(-1);
    }

    if (loadingInitial || submitting)
        return <LoadingComponent content="Loading activity..." />;

    return (
        <div className="w-full flex flex-col mt-2">
            <form onSubmit={handleSubmit} autoComplete="off" className="w-full">
                <div className="w-full flex flex-col gap-4">
                    <span className="text-[20px] font-medium text-decoration-line: underline">General Information</span>

                    <NewToggleSwitch value={record.status} record={record} setRecord={setRecord} name={"status"} isRequired={true} title="Status" />
                    <InputElement
                        handleValidation={handleValidationChange}
                        value={record.name}
                        title={"Location Group Name"}
                        error={errors.name}
                        name={"name"}
                        isRequired={true}
                        record={record}
                        setRecord={setRecord}
                    />

                    <SelectElement
                        handleValidation={handleValidationChange}
                        record={record}
                        setRecord={(updatedRecord) => {
                            const projectIdValue = (updatedRecord as any).projectId;
                            if (projectIdValue === "placeholder" || projectIdValue === "" || projectIdValue === 0 || projectIdValue === null || projectIdValue === undefined) {
                                setRecord({ ...updatedRecord, projectId: 0 });
                            } else {
                                setRecord(updatedRecord);
                            }
                        }}
                        name={"projectId"}
                        title={"Project"}
                        options={projectList}
                        defValue={record.projectId && record.projectId > 0 ? record.projectId : "placeholder"}
                        isWithApi={true}
                        isRequired={true}
                        error={errors.projectId}
                    />

                    <InputElement
                        value={record.remark}
                        title={"Remark"}
                        name={"remark"}
                        isRequired={false}
                        record={record}
                        setRecord={setRecord}
                    />

                    <span className="text-[20px] font-medium text-decoration-line: underline mt-4">Threshold Settings</span>

                    <InputElement
                        value={record.alertThreshold?.toString() || ""}
                        title={"Alert Threshold"}
                        name={"alertThreshold"}
                        isRequired={false}
                        record={record}
                        setRecord={(updatedRecord) => {
                            const value = (updatedRecord as any).alertThreshold;
                            setRecord({
                                ...updatedRecord,
                                alertThreshold: value === "" || value === null || value === undefined ? undefined : parseFloat(value)
                            });
                        }}
                        type="number"
                        step="0.001"
                    />

                    <InputElement
                        value={record.alarmThreshold?.toString() || ""}
                        title={"Alarm Threshold"}
                        name={"alarmThreshold"}
                        isRequired={false}
                        record={record}
                        setRecord={(updatedRecord) => {
                            const value = (updatedRecord as any).alarmThreshold;
                            setRecord({
                                ...updatedRecord,
                                alarmThreshold: value === "" || value === null || value === undefined ? undefined : parseFloat(value)
                            });
                        }}
                        type="number"
                        step="0.001"
                    />

                    <InputElement
                        value={record.actionThreshold?.toString() || ""}
                        title={"Action Threshold"}
                        name={"actionThreshold"}
                        isRequired={false}
                        record={record}
                        setRecord={(updatedRecord) => {
                            const value = (updatedRecord as any).actionThreshold;
                            setRecord({
                                ...updatedRecord,
                                actionThreshold: value === "" || value === null || value === undefined ? undefined : parseFloat(value)
                            });
                        }}
                        type="number"
                        step="0.001"
                    />

                    <div className="flex flex-col min-[540px]:flex-row justify-center gap-1 min-[540px]:gap-5 mt-5">
                        <IButton type="submit" text={selectedId ? "Update" : "Create"} />
                        <IButton type="button" onClick={handleCancel} text="Cancel" backgroundColor="[#FFFFFF]" textColor="[#15304B]" borderColor="nav-blue" />
                    </div>

                    <ServerErrorMessage messageHeader={serverError?.messageHeader} messageList={serverError?.messageList} />
                </div>
            </form>

            {/* 編輯模式下顯示 Locations 列表 */}
            {selectedId && locations.length > 0 && (
                <div className="w-full mt-8">
                    <span className="text-[20px] font-medium text-decoration-line: underline mb-4 block">
                        Locations in this Group ({locations.length})
                    </span>
                    <ThemeProvider theme={customTableTheme}>
                        <CoustomTable
                            page={locationPage}
                            setPage={setLocationPage}
                            subPath="location"
                            headers={[
                                { id: "name", data: "Location Name" },
                                { id: "projectName", data: "Project Name" },
                                { id: "loggerSetName", data: "Logger Set Name" },
                                { id: "loggerSN", data: "Logger SN" },
                                { id: "address", data: "Address" },
                                { id: "status", data: "Status" }
                            ]}
                            rows={locationRows}
                            activeId={activeId}
                            direction={direction}
                            onSortClick={() => {
                                setLocationPage(0);
                                // Add sorting logic if needed
                            }}
                            rowsPerPage={10}
                        />
                    </ThemeProvider>
                </div>
            )}

            {selectedId && locations.length === 0 && (
                <div className="w-full mt-8">
                    <span className="text-[20px] font-medium text-decoration-line: underline mb-4 block">
                        Locations in this Group
                    </span>
                    <div className="text-gray-500 italic mt-4">
                        No locations are currently assigned to this group.
                    </div>
                </div>
            )}
        </div>
    );
});

